import React, {useState, useMemo, useRef} from 'react';
import { Redirect } from 'react-router-dom';
import { withStyles, Typography, Tooltip, IconButton } from '@material-ui/core';
import { connect } from 'react-redux';
import PrinterList from './PrinterList';
import { hasPermission, createHistory, hasAtLeastOnePermission } from '../../sc-utilities';
import { FindInPage } from '@material-ui/icons';

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    "@global": {
        "[class*='online']": {
            color: theme.palette.success.dark
        },
        "[class*='offline']": {
            color: theme.palette.error.dark
        }
    },
    progressBarr: {
        width: '100%',
        height: 5
    },
    online: {
        color: theme.palette.success.dark
    },
    offline: {
        color: theme.palette.error.dark
    }
});

const Printer = props => {
    const tableRef = useRef();
    const { classes, columns, translations, permissions } = props;
    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, printerID: null, redirectPath: '' });

    let visible = columns && columns['Printer'];

    const tableColumns = useMemo(() => {
        return [{
            title: translations["action"] || "Action", field: 'Action',
            hidden: visible ? visible['Action'] : false,
            cellStyle: { minWidth: 200, maxWidth: 200 },
            filtering: false,
            render: rowData => <>
                {
                    hasPermission(permissions, 'printer_load') ?
                        <Tooltip key={`Edit-${rowData.PrinterID}`} title={translations["printer_details"] || "Printer details"}>
                            <IconButton
                                id={`printer_details_${rowData.PrinterID}`}
                                component='div'
                                onClick={() => redirectHandler(`/settings/printer/edit?PrinterID=${rowData.PrinterID}`)}
                            >
                                <FindInPage />
                            </IconButton>
                        </Tooltip> : null
                }
            </>
        },
        {
            title: translations["name"] || 'Name', field: "Name",
            hidden: visible ? visible['Name'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Name ? rowData.Name : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["printer_type"] || 'Printer Type', field: 'PrinterType',
            hidden: visible ? visible['PrinterType'] : false,
            filtering: true,
            lookup: { 'zebra': 'Zebra', 'godex': 'Godex', 'brother': 'Brother' },
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.PrinterType ? rowData.PrinterType : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["connection_type"] || 'Connection Type', field: 'ConnectionType',
            hidden: visible ? visible['ConnectionType'] : false,
            filtering: true,
            lookup: { 'network': translations['network'] || 'Network', 'usb': translations['usb'] || 'USB' },
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.ConnectionType ? rowData.ConnectionType : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["uri"] || 'URI', field: "URI",
            hidden: visible ? visible['URI'] : false,
            filtering: false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.URI ? rowData.URI : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["control_machine"] || 'Control machine', field: "ControlMachine",
            hidden: visible ? visible['ControlMachine'] : false,
            filtering: false,
            render: rowData => {
                if (rowData.ControlMachines instanceof Array && rowData.ControlMachines.length === 1) {
                    return <Typography variant={'caption'}>{rowData.ControlMachines[0].Name}</Typography>;
                }
                return <Typography variant={'caption'}>{translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["status"] || 'Status', field: "Status",
            hidden: visible ? visible['Status'] : false,
            filtering: false,
            render: rowData => {
                if (rowData.ConnectionType === 'network') {
                    if (rowData.ControlMachines instanceof Array && rowData.ControlMachines.length === 1) {
                        let controlMachinePrinters = rowData.ControlMachines[0].ControlMachinePrinters;
                        if (controlMachinePrinters && controlMachinePrinters.Status === 'online') {
                            return <CustomChip key={`status_${rowData.PrinterID}`} className={'online'} value={translations["online"] || "Online"} />;
                        }
                    }
                    return <CustomChip key={`status_${rowData.PrinterID}`} className={'offline'} value={translations["offline"] || "Offline"} />;
                } else if (rowData.ConnectionType === 'usb') {
                    return <Typography variant={'caption'}>{translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            }
        }
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes, translations, visible])

    const CustomChip = props => {
        const { keyValue, value, className } = props;
        return (
            <Typography key={keyValue} className={className}>{value}</Typography>
        );
    };

    if (!hasAtLeastOnePermission(permissions, ['printer_load'])) {
        tableColumns.shift();
    }

    const redirectHandler = (path, printerID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, printerID: printerID, redirectPath: path })
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    return (
        <div className={classes.root}>
            {
                hasPermission(permissions, 'printer_list') ?
                    <PrinterList
                        tableColumns={tableColumns}
                        redirectHandler={redirectHandler}
                        tableRef={tableRef}
                    /> : null
            }
        </div >
    );
}

const mapStateToProps = state => {
    return {
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || []
    };
};

export default connect(
    mapStateToProps,
    null
)(withStyles(styles, { withTheme: true })(Printer));